import React, { useEffect, useState } from 'react';
import { Modal } from '../shared/Modal';
import { SectionConfig, ObjectType } from '../../types/metadata';
import { Info } from 'lucide-react';
interface SectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (section: SectionConfig | Omit<SectionConfig, 'id'>) => void;
  initialData?: SectionConfig;
}
export function SectionModal({
  isOpen,
  onClose,
  onSave,
  initialData
}: SectionModalProps) {
  const [formData, setFormData] = useState<Partial<SectionConfig>>({
    sectionKey: '',
    sectionLabel: '',
    objectType: ObjectType.Lead,
    order: 1,
    active: true
  });
  useEffect(() => {
    if (initialData && isOpen) {
      setFormData(initialData);
    } else if (isOpen) {
      setFormData({
        sectionKey: '',
        sectionLabel: '',
        objectType: ObjectType.Lead,
        order: 1,
        active: true
      });
    }
  }, [initialData, isOpen]);
  const generateKey = (label: string) => {
    return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
  };
  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const label = e.target.value;
    setFormData(prev => ({
      ...prev,
      sectionLabel: label,
      // Auto-generate key only if creating new and key hasn't been manually edited much
      sectionKey: !initialData && (!prev.sectionKey || prev.sectionKey === generateKey(prev.sectionLabel || '')) ? generateKey(label) : prev.sectionKey
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.sectionKey || !formData.sectionLabel || !formData.objectType) return;
    onSave(formData as SectionConfig | Omit<SectionConfig, 'id'>);
    onClose();
  };
  const footer = <>
      <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-401">
        Cancel
      </button>
      <button type="submit" form="section-form" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-402">
        {initialData ? 'Save Changes' : 'Create Section'}
      </button>
    </>;
  return <Modal isOpen={isOpen} onClose={onClose} title={initialData ? 'Edit Section' : 'New Section'} size="md" footer={footer} data-id="element-403">
      <form id="section-form" onSubmit={handleSubmit} className="space-y-5" data-id="element-404">
        <div data-id="element-405">
          <label htmlFor="sectionLabel" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-406">
            Section Label <span className="text-red-500" data-id="element-407">*</span>
          </label>
          <input type="text" id="sectionLabel" required value={formData.sectionLabel || ''} onChange={handleLabelChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g., Borrower Information" data-id="element-408" />
        </div>

        <div data-id="element-409">
          <label htmlFor="sectionKey" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-410">
            Section Key <span className="text-red-500" data-id="element-411">*</span>
          </label>
          <input type="text" id="sectionKey" required value={formData.sectionKey || ''} onChange={e => setFormData({
          ...formData,
          sectionKey: e.target.value
        })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono text-gray-600" placeholder="e.g., borrower_info" data-id="element-412" />
          <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1" data-id="element-413">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" data-id="element-414" />
            Unique identifier used in field mappings. Use lowercase letters,
            numbers, and underscores.
          </p>
        </div>

        <div data-id="element-415">
          <label htmlFor="objectType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-416">
            Target Object <span className="text-red-500" data-id="element-417">*</span>
          </label>
          <select id="objectType" required value={formData.objectType || ObjectType.Lead} onChange={e => setFormData({
          ...formData,
          objectType: e.target.value as ObjectType
        })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white" data-id="element-418">
            {Object.values(ObjectType).map(type => <option key={type} value={type} data-id="element-419">
                {type}
              </option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-420">
          <div data-id="element-421">
            <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-422">
              Display Order
            </label>
            <input type="number" id="order" min="1" value={formData.order || 1} onChange={e => setFormData({
            ...formData,
            order: parseInt(e.target.value) || 1
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-423" />
          </div>

          <div className="flex items-center pt-6" data-id="element-424">
            <label className="flex items-center cursor-pointer" data-id="element-425">
              <div className="relative" data-id="element-426">
                <input type="checkbox" className="sr-only" checked={formData.active ?? true} onChange={e => setFormData({
                ...formData,
                active: e.target.checked
              })} data-id="element-427" />
                <div className={`block w-10 h-6 rounded-full transition-colors ${formData.active ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-428"></div>
                <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.active ? 'transform translate-x-4' : ''}`} data-id="element-429"></div>
              </div>
              <span className="ml-3 text-sm font-medium text-gray-700" data-id="element-430">
                Active
              </span>
            </label>
          </div>
        </div>
      </form>
    </Modal>;
}